import type BaseEnumType from "@/app/services/baseEnumType";
import type { Maybe } from "@/components/utils/typeHelpers";

export class EnumCostPriceUpdateStrategyType implements BaseEnumType {
  private static allValues: EnumCostPriceUpdateStrategyType[] = []; // make sure this is top-most

  /**
   * Suggests the latest cost price seen for a stock item as the new cost price.
   */
  public static latestCostPrice = new EnumCostPriceUpdateStrategyType(
    0,
    "LatestCostPrice",
    "enum_cost_price_update_strategy_latest"
  );

  /**
   * "The Deya model"
   *
   * Suggested cost price comes from applying the new cost price and quantity on top
   * of the current stock price and in-stock quantity. The effect on the cost price
   * will be proportional to the amount of stock being "diluted" into the main stock
   * pool.
   *
   * e.g. Current stock (qty=100, unitcostprice=10), New stock being applied (qty=10, unitcostprice=20)
   * New suggested cost price = (100*10 + 10*20) / 110 = 10.91.
   */
  public static runningAverageCostPrice = new EnumCostPriceUpdateStrategyType(
    1,
    "RunningAverageCostPrice",
    "enum_cost_price_update_strategy_running_average"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumCostPriceUpdateStrategyType.allValues.push(this);
  }

  public equals(other: Maybe<EnumCostPriceUpdateStrategyType>): boolean {
    return this.key === other?.key;
  }

  public isLatestCostPrice() {
    return this.equals(EnumCostPriceUpdateStrategyType.latestCostPrice);
  }
  public isRunningAverageCostPrice() {
    return this.equals(EnumCostPriceUpdateStrategyType.runningAverageCostPrice);
  }

  public static getValues(): EnumCostPriceUpdateStrategyType[] {
    return EnumCostPriceUpdateStrategyType.allValues;
  }

  public static getByKey(key: number): EnumCostPriceUpdateStrategyType {
    const found = EnumCostPriceUpdateStrategyType.allValues.find(
      (e) => e.key === key
    );
    if (found === undefined) {
      throw new Error(
        `EnumCostPriceUpdateStrategyType - Key not found: ${key}`
      );
    }

    return found;
  }
}
